// What the contacts screen says about its numbers (UI-9), apart from how it
// draws them, so tests/admin can hold the words and the arithmetic on Node.
//
// Two places. Over the list: a tab per country with its count — the owner
// reads these first — and the chosen tab's figures, from one grouped count
// the server keeps for a few minutes (`GET /contacts/summary`). On the detail:
// the address in every country we hold it, each country's own verdict.

import type { ContactCounts, ContactDetail, ContactSummary } from "@/lib/api";
import { count, minute, shareOf } from "@/components/emc/format";
import { labelOf } from "@/components/emc/status";

export interface CountryTab {
  /** The list's `country` filter: "" for every country. */
  value: string;
  label: string;
  /** The tab's count, formatted; null until the summary is in. */
  count: string | null;
}

/** The twelve Klaviyo countries in the server's order (`emc_db.handover.
 * KLAVIYO_COUNTRIES`, held equal by tests/admin): the tabs before the
 * summary is in. */
export const KNOWN_COUNTRIES = ["RO", "PL", "HU", "GR", "SK", "EU", "BG", "CZ", "IT", "HR", "NL", "SI"];

/** The country a `?orszag=` asks for: two letters, upper-cased, or "" for
 * every country. The list API takes two characters at most and answers
 * anything longer with a 422, so a garbled or hand-typed link opens on
 * "Összes" instead of on an error. */
export function countryParam(raw: string | null | undefined): string {
  return raw && /^[A-Za-z]{2}$/.test(raw) ? raw.toUpperCase() : "";
}

/** "Összes" first, then each country the summary names, in its order. With
 * no summary yet — it can take its minute, or fail — the tabs are still
 * there, they are the list's country filter: the twelve countries, without a
 * number — not a row of zeros, which would read as an empty base. */
export function countryTabs(summary: ContactSummary | null): CountryTab[] {
  const all: CountryTab = {
    value: "",
    label: "Összes",
    count: summary === null ? null : count(summary.overall.total),
  };
  if (summary === null) {
    return [all, ...KNOWN_COUNTRIES.map((code) => ({ value: code, label: code, count: null }))];
  }
  return [
    all,
    ...summary.countries.map((row) => ({ value: row.country, label: row.country, count: count(row.total) })),
  ];
}

/** The figures of one tab: the whole summary, or one country's row. A
 * country the summary does not name (a tab picked from an old link) is
 * null, not zeros. */
export function countsFor(summary: ContactSummary | null, country: string): ContactCounts | null {
  if (summary === null) return null;
  if (country === "") return summary.overall;
  return summary.countries.find((row) => row.country === country) ?? null;
}

export interface Figure {
  key: keyof ContactCounts;
  label: string;
  value: string;
  /** "az összes 52%-a" — what the number is measured against; null on the
   * total itself, and where there is nothing to measure against. */
  comparison: string | null;
}

/** Összes · feliratkozott · leiratkozott · soha nem iratkozott fel ·
 * (megerősítésre vár, only when there is any) · kizárva · (nincs jelen a
 * Klaviyóban, when the API counts it) · küldhető.
 *
 * The consent figures add up to the total; "kizárva" cuts across them (an
 * exclusion is on the address, whatever the row's consent says), and so does
 * "nincs jelen" (UI-10: the profile is gone from the country's Klaviyo
 * account, whatever the row's consent was); "küldhető" is what is left to
 * mail. An API older than `absent` leaves that figure out rather than
 * showing a 0, which would read as "everybody is still there". */
export function summaryFigures(counts: ContactCounts): Figure[] {
  const of = (part: number) => shareOf(part, counts.total, "az összes");
  const figures: Figure[] = [
    { key: "total", label: "Összes", value: count(counts.total), comparison: null },
    { key: "subscribed", label: "Feliratkozott", value: count(counts.subscribed), comparison: of(counts.subscribed) },
    {
      key: "unsubscribed",
      label: "Leiratkozott",
      value: count(counts.unsubscribed),
      comparison: of(counts.unsubscribed),
    },
    {
      key: "never_subscribed",
      label: "Soha nem iratkozott fel",
      value: count(counts.never_subscribed),
      comparison: of(counts.never_subscribed),
    },
  ];
  if (counts.pending > 0) {
    figures.push({
      key: "pending",
      label: "Megerősítésre vár",
      value: count(counts.pending),
      comparison: of(counts.pending),
    });
  }
  figures.push({
    key: "suppressed",
    label: "Kizárva",
    value: count(counts.suppressed),
    comparison: of(counts.suppressed),
  });
  if (typeof counts.absent === "number") {
    figures.push({
      key: "absent",
      label: "Nincs jelen a Klaviyóban",
      value: count(counts.absent),
      comparison: of(counts.absent),
    });
  }
  figures.push({
    key: "sendable",
    label: "Küldhető",
    value: count(counts.sendable),
    comparison: of(counts.sendable),
  });
  return figures;
}

/** "frissítve 14:05" — when the server read the figures, on the reader's
 * clock. The summary is kept for minutes; saying so is what keeps a kept
 * figure from passing for a live one. */
export function refreshedAt(computedAt: string | null | undefined): string | null {
  if (!computedAt) return null;
  const moment = new Date(computedAt);
  if (Number.isNaN(moment.getTime())) return null;
  return `frissítve ${moment.toLocaleTimeString("hu-HU", { hour: "2-digit", minute: "2-digit" })}`;
}

// --- the address in every country --------------------------------------------

/** A row's presence in its country's Klaviyo account, as the `presence`
 * pill's state (UI-10). Only an explicit `false` is absent: an API older than
 * the field leaves it out, and that reads as present — the sendable verdict
 * beside it is the server's either way. */
export function presenceOf(present: boolean | null | undefined): "present" | "absent" {
  return present === false ? "absent" : "present";
}

export interface CountryRow {
  country: string;
  contactId: number;
  /** The row this page is open on. */
  current: boolean;
  consent: string;
  sendable: boolean;
  /** Whether the profile is still in that country's Klaviyo account. */
  presence: "present" | "absent";
  /** Why it is excluded there, in words; null when it is not. `until` is a
   * hold's end (rule 1, 2026-10-16), null on an exclusion for good. */
  exclusion: {
    reason: string;
    scope: string;
    source: string;
    since: string;
    until: string | null;
  } | null;
}

/** A hold reads "tartás: <cause>"; every other reason is its own word. */
function reasonWords(reason: string, cause: string | null | undefined): string {
  const word = labelOf("suppressionReason", reason);
  return reason === "held" && cause ? `${word}: ${labelOf("holdCause", cause)}` : word;
}

/** One row per country the address is held in, or null when the API does
 * not send the field (an API older than UI-9): the screen then shows
 * nothing, rather than an empty table that would claim "in no country".
 *
 * A row's `suppression` left out (an API that drops empty fields) reads as
 * null, and a `sendable` that is not `true` reads as "no" — a missing
 * verdict never reads as permission. A missing `present` reads as present
 * (`presenceOf`). */
export function countryRows(contact: ContactDetail): CountryRow[] | null {
  if (!Array.isArray(contact.countries)) return null;
  return contact.countries.map((row) => ({
    country: row.country,
    contactId: row.contact_id,
    current: row.contact_id === contact.id,
    consent: labelOf("consent", row.consent_status),
    sendable: row.sendable === true,
    presence: presenceOf(row.present),
    exclusion:
      row.suppression == null
        ? null
        : {
            reason: reasonWords(row.suppression.reason, row.suppression.cause),
            scope: labelOf("suppressionScope", row.suppression.scope),
            source: labelOf("suppressionSource", row.suppression.source),
            since: minute(row.suppression.since),
            until: row.suppression.until ? minute(row.suppression.until) : null,
          },
  }));
}
